if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButtons = Array.from(this.querySelectorAll('[type="submit"]'));
        this.submitButton = this.submitButtons[0];
        this.submitButtonText = this.submitButton.querySelector('span');
        this.submitButtonDefaultText = this.submitButtonText?.textContent;
        this.redirectToCheckout = this.dataset.redirectToCheckout === 'true';
        this.productInfo = this.closest('product-info');
        this.largeOrderCta = this.querySelector('[data-large-order-cta]');
        this.buttonsContainer = this.querySelector('.product-form__buttons');
        this.usbAddon = null;
        this.usbAddonInput = null;
        this.usbAddonChecked = false;
        this.currentVariant = this.getInitialVariant();
        this.handleUsbAddonChange = this.handleUsbAddonChange.bind(this);
        this.largeOrderControls = this.largeOrderCta || this.buttonsContainer || this.usbAddon;
        this.largeOrderUnsubscriber = null;
        this.refreshUsbAddon();

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        if (this.largeOrderControls) {
          this.toggleLargeOrderQuote(this.isLargeOrderSelected());
          this.largeOrderUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
            if (data.sectionId !== this.dataset.sectionId) return;
            this.currentVariant = data.variant || null;
            this.refreshUsbAddon();
            this.toggleLargeOrderQuote(data.variant?.option1 === '2000+' || this.isLargeOrderSelected());
          });
        }
      }

      disconnectedCallback() {
        this.largeOrderUnsubscriber?.();
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        const submitter = evt.submitter || this.submitButton;
        const redirectToCheckout =
          this.redirectToCheckout || submitter.dataset.redirectToCheckoutButton === 'true';
        const redirectToCart = submitter.dataset.redirectToCartButton === 'true';

        if (submitter.getAttribute('aria-disabled') === 'true') return;
        if (this.isLargeOrderSelected()) {
          this.toggleLargeOrderQuote(true);
          return;
        }

        this.handleErrorMessage();

        this.submitButtons.forEach((button) => button.setAttribute('aria-disabled', true));
        submitter.classList.add('loading');
        submitter.querySelector('.loading__spinner')?.classList.remove('hidden');

        const formData = new FormData(this.form);
        const selectedVariantId = formData.get('id');
        this.refreshUsbAddon();
        const usbAddonInput = this.usbAddonInput?.checked ? this.usbAddonInput : null;
        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';

        if (this.cart) {
          this.cart.setActiveElement(document.activeElement);
        }

        if (usbAddonInput) {
          const quantity = Number(formData.get('quantity') || 1);
          const body = {
            items: [
              { id: selectedVariantId, quantity },
              { id: usbAddonInput.dataset.usbAddonVariantId, quantity: 1 },
            ],
          };

          if (this.cart) {
            body.sections = this.cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
          }

          config.body = JSON.stringify(body);
        } else {
          delete config.headers['Content-Type'];

          if (this.cart) {
            formData.append(
              'sections',
              this.cart.getSectionsToRender().map((section) => section.id)
            );
            formData.append('sections_url', window.location.pathname);
          }

          config.body = formData;
        }

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              window.MementoScanAnalytics?.trackAddToBasketError({
                source: 'product-form',
                selectedVariantId,
                response,
              });

              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: selectedVariantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description);

              const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            }

            window.MementoScanAnalytics?.trackAddToBasket(response, {
              source: 'product-form',
              selectedVariantId,
              redirectToCart,
              redirectToCheckout,
            });

            if (redirectToCheckout) {
              window.location.href = window.routes.checkout_url || '/checkout';
              return;
            } else if (redirectToCart) {
              window.location.href = window.routes.cart_url;
              return;
            } else if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: selectedVariantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
          })
          .finally(() => {
            this.submitButtons.forEach((button) => button.classList.remove('loading'));
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error) this.submitButtons.forEach((button) => button.removeAttribute('aria-disabled'));
            this.querySelectorAll('.loading__spinner').forEach((spinner) => spinner.classList.add('hidden'));

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButtons.forEach((button) => button.setAttribute('disabled', 'disabled'));
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButtons.forEach((button) => button.removeAttribute('disabled'));
          this.submitButtonText.textContent = this.submitButtonDefaultText || window.variantStrings.addToCart;
        }
      }

      isLargeOrderSelected() {
        if (this.currentVariant?.option1 === '2000+') return true;
        const formId = this.form?.getAttribute('id');
        if (!formId) return false;
        const selector = `input[form="${CSS.escape(formId)}"][type="radio"][value="2000+"]:checked`;
        return Boolean(document.querySelector(selector));
      }

      getInitialVariant() {
        const variantJson = this.productInfo?.querySelector('variant-selects [data-selected-variant]')?.textContent;
        if (!variantJson) return null;

        try {
          return JSON.parse(variantJson);
        } catch (_error) {
          return null;
        }
      }

      toggleLargeOrderQuote(isLargeOrder) {
        this.productInfo?.classList.toggle('photo-product--large-order-selected', isLargeOrder);
        if (this.largeOrderCta) this.largeOrderCta.hidden = !isLargeOrder;
        if (this.buttonsContainer) this.buttonsContainer.hidden = isLargeOrder;
        this.refreshUsbAddon();
        if (this.usbAddon) {
          this.usbAddon.hidden = isLargeOrder;
        }
        if (this.usbAddonInput) {
          this.usbAddonInput.disabled = isLargeOrder;
          if (isLargeOrder) {
            this.usbAddonInput.checked = false;
            this.usbAddonChecked = false;
          } else if (this.usbAddonChecked) {
            this.usbAddonInput.checked = true;
          }
        }
        this.updateSelectionSummary();
      }

      refreshUsbAddon() {
        const input = this.productInfo?.querySelector('[data-usb-addon-variant-id]') || null;
        this.usbAddon = input?.closest('.photo-product-usb-pick') || null;

        if (input && input !== this.usbAddonInput) {
          this.usbAddonInput = input;
          this.usbAddonInput.addEventListener('change', this.handleUsbAddonChange);
          if (this.usbAddonChecked && !this.usbAddonInput.disabled) {
            this.usbAddonInput.checked = true;
          }
        } else if (!input) {
          this.usbAddonInput = null;
        }

        this.largeOrderControls = this.largeOrderCta || this.buttonsContainer || this.usbAddon;
        this.updateSelectionSummary();
      }

      handleUsbAddonChange(event) {
        this.usbAddonChecked = event.target.checked;
        this.updateSelectionSummary();
      }

      updateSelectionSummary() {
        const summary = this.productInfo?.querySelector('[data-selection-summary]');
        if (!summary) return;

        const photos = summary.querySelector('[data-selection-photos]');
        const resolution = summary.querySelector('[data-selection-resolution]');
        const deliveryRow = summary.querySelector('[data-selection-delivery-row]');
        const delivery = summary.querySelector('[data-selection-delivery]');
        const usbRow = summary.querySelector('[data-usb-summary-row]');
        const usbValue = summary.querySelector('[data-usb-summary-value]');
        const total = summary.querySelector('[data-selection-total]');
        const variant = this.currentVariant;
        const usbChecked = Boolean(this.usbAddonInput?.checked && !this.usbAddonInput.disabled);

        if (variant) {
          if (photos) photos.textContent = variant.option1 || '';
          if (resolution) resolution.textContent = this.formatResolutionLabel(variant.option2);
          if (delivery) delivery.textContent = variant.option3 || '';
          if (deliveryRow) deliveryRow.hidden = !variant.option3;
        }
        if (usbRow) usbRow.hidden = false;
        if (usbValue) {
          usbValue.textContent = usbChecked ? this.usbAddonInput.dataset.usbAddonPrice : 'Not selected';
        }
        if (!total) return;

        const basePriceCents = Number(variant?.price ?? total.dataset.basePriceCents ?? 0);
        if (variant?.price) total.dataset.basePriceCents = variant.price;
        const usbPriceCents = usbChecked ? Number(this.usbAddonInput?.dataset.usbAddonPriceCents || 0) : 0;
        total.textContent = this.formatMoneyWithCurrency(basePriceCents + usbPriceCents, summary.dataset.currencyIsoCode);
      }

      formatResolutionLabel(resolution) {
        if (resolution === '300dpi') return '300dpi (Standard Sharing)';
        if (resolution === '600dpi') return '600dpi (Enhanced Detail)';
        if (resolution === '1200dpi' || resolution === '1200dpi (interpolated)') return '1200dpi (interpolated)';
        return resolution || '';
      }

      formatMoneyWithCurrency(cents, currencyCode) {
        const currency = currencyCode || window.Shopify?.currency?.active || 'GBP';
        try {
          const amount = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
            style: 'currency',
            currency,
          }).format(cents / 100);
          return currency ? `${amount} ${currency}` : amount;
        } catch (_error) {
          return `£${(cents / 100).toFixed(2)}${currency ? ` ${currency}` : ''}`;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
